import { supabase } from '../lib/supabase';

// License types
export type LicenseType = 'basic' | 'club';

// License status
export type LicenseStatus = 'active' | 'expired' | 'cancelled';

// License interface
export interface UserLicense {
  id: number;
  user_id: string;
  license_type: LicenseType;
  status: LicenseStatus;
  expires_at?: string;
  created_at: string;
  updated_at: string;
  features: LicenseFeatures;
  global_admin?: boolean;
}

// Features available with each license
export interface LicenseFeatures {
  canCreateClubs: boolean;
  maxClubs: number;
  canCreateTournaments: boolean;
  maxTournaments: number;
  canInviteMembers: boolean;
  maxMembersPerClub: number;
  analytics: boolean;
  customBranding: boolean;
  globalAdmin: boolean;
}

// License configurations
export const LICENSE_CONFIGS: Record<LicenseType, LicenseFeatures> = {
  basic: {
    canCreateClubs: false,
    maxClubs: 0,
    canCreateTournaments: false,
    maxTournaments: 0,
    canInviteMembers: false,
    maxMembersPerClub: 0,
    analytics: false,
    customBranding: false,
    globalAdmin: false,
  },
  club: {
    canCreateClubs: true,
    maxClubs: -1, // -1 means unlimited
    canCreateTournaments: true,
    maxTournaments: -1, // unlimited
    canInviteMembers: true,
    maxMembersPerClub: -1, // unlimited
    analytics: true,
    customBranding: false,
    globalAdmin: false,
  }
};

/**
 * Get user's current license
 */
export const getUserLicense = async (): Promise<UserLicense | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    return null;
  }

  // Check for active license
  const { data: license, error: licenseError } = await supabase
    .from('user_licenses')
    .select('*')
    .eq('user_id', user.id)
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  // If table doesn't exist yet, return basic license
  if (licenseError && licenseError.code === 'PGRST116') {
    return {
      id: 0,
      user_id: user.id,
      license_type: 'basic',
      status: 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      features: LICENSE_CONFIGS.basic
    };
  }

  if (!license) {
    // Return default basic license if no license found
    return {
      id: 0,
      user_id: user.id,
      license_type: 'basic',
      status: 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      features: LICENSE_CONFIGS.basic
    };
  }

  // Check if license is expired
  if (license.expires_at && new Date(license.expires_at) < new Date()) {
    // Update license status to expired
    await supabase
      .from('user_licenses')
      .update({ status: 'expired' })
      .eq('id', license.id);

    // Return basic license if expired
    return {
      id: license.id,
      user_id: user.id,
      license_type: 'basic',
      status: 'expired',
      created_at: license.created_at,
      updated_at: new Date().toISOString(),
      features: LICENSE_CONFIGS.basic
    };
  }

  // Create features based on license type and global admin status
  const baseFeatures = LICENSE_CONFIGS[license.license_type as LicenseType];
  const features: LicenseFeatures = {
    ...baseFeatures,
    globalAdmin: license.global_admin || false
  };

  return {
    ...license,
    features
  };
};

/**
 * Check if user can create clubs based on their license
 */
export const canCreateClub = async (): Promise<{ 
  canCreate: boolean; 
  reason?: string; 
  licenseType: LicenseType;
  currentCount?: number;
  maxClubs?: number;
}> => {
  const license = await getUserLicense();
  
  if (!license) {
    return {
      canCreate: false,
      reason: 'No license found',
      licenseType: 'basic'
    };
  }

  if (license.license_type === 'basic') {
    return {
      canCreate: false,
      reason: 'Club creation requires a Club license',
      licenseType: 'basic'
    };
  }

  if (!license.features.canCreateClubs) {
    return {
      canCreate: false,
      reason: 'Club creation is not allowed with your current license',
      licenseType: license.license_type
    };
  }

  // Check club count limit
  const currentCount = await getUserClubCount();
  
  if (license.features.maxClubs > 0 && currentCount >= license.features.maxClubs) {
    return {
      canCreate: false,
      reason: `You have reached the maximum limit of ${license.features.maxClubs} clubs`,
      licenseType: license.license_type,
      currentCount,
      maxClubs: license.features.maxClubs
    };
  }

  return {
    canCreate: true,
    licenseType: license.license_type,
    currentCount,
    maxClubs: license.features.maxClubs
  };
};

/**
 * Get the number of clubs a user has created
 */
export const getUserClubCount = async (): Promise<number> => {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) {
    return 0;
  }

  const { count } = await supabase
    .from('clubs')
    .select('*', { count: 'exact', head: true })
    .eq('created_by', user.id);

  return count || 0;
};

/**
 * Check if current user is a global administrator
 */
export const isGlobalAdmin = async (): Promise<boolean> => {
  const license = await getUserLicense();
  return license?.features.globalAdmin || false;
};

/**
 * Get license information for display
 */
export const getLicenseInfo = async (): Promise<{
  licenseType: LicenseType;
  status: LicenseStatus;
  features: LicenseFeatures;
  expiresAt?: string;
}> => {
  const license = await getUserLicense();
  
  if (!license) {
    return {
      licenseType: 'basic',
      status: 'active',
      features: LICENSE_CONFIGS.basic
    };
  }

  return {
    licenseType: license.license_type,
    status: license.status,
    features: license.features,
    expiresAt: license.expires_at
  };
};
